/**
 * @jeanieiq/types
 *
 * Shared TypeScript types used across the monorepo. Session 0.1 seeds this
 * package with the minimum types referenced by the other packages. Feature
 * domains add their own types in later sessions.
 */

// ------------------------------------------------------------------------
// API response envelope — CLAUDE.md Coding Standards § API Responses
// ------------------------------------------------------------------------

export type ApiSuccess<T> = {
  success: true;
  data: T;
};

export type ApiError = {
  success: false;
  error: {
    code: string;
    message: string;
    /** Optional structured details for client handling (e.g. validation errors) */
    details?: Record<string, unknown>;
  };
};

export type ApiResponse<T> = ApiSuccess<T> | ApiError;

// ------------------------------------------------------------------------
// Result pattern — CLAUDE.md Coding Standards § TypeScript
// ------------------------------------------------------------------------

export type Ok<T> = { ok: true; value: T };
export type Err<E> = { ok: false; error: E };
export type Result<T, E = Error> = Ok<T> | Err<E>;

export const ok = <T>(value: T): Ok<T> => ({ ok: true, value });
export const err = <E>(error: E): Err<E> => ({ ok: false, error });

// ------------------------------------------------------------------------
// Subscription tier — 3-tier model (Apr 2026 platform-wide migration)
// ------------------------------------------------------------------------
//
// The platform was migrated from the legacy 4-tier vocabulary
// (essentials / plus / premium / professional) to the 3-tier vocabulary
// (base / plus / customised) on 2026-04-26.
//
//   • base    — base full-platform tier, all operational modules
//                  included. Replaces legacy 'essentials', 'premium' and
//                  'professional' (semantic flip: old 'professional' was
//                  the everything-included top tier; new 'base' is the
//                  everything-included BASE tier).
//   • plus       — paid add-on layer. Adds ISO 45001, FORS, advanced
//                  Fleet calc, CIA dashboards, Accreditations management.
//   • customised — bespoke enterprise tier (white-label, custom
//                  integrations, multi-site).
//
// Migration commit: see migration `<ts>_tier_3tier_migration` and the
// `update-2026-04-findings.md` log. Existing tenant data backfilled:
// essentials/premium/professional → base; plus stays plus.
// ------------------------------------------------------------------------

// ------------------------------------------------------------------------
// STATUTORY PAY TYPES — one list, because there were four.
//
// PAYROLL-SPBP-UNREACHABLE-SHPP-PRICED-AS-SPP-1321. The same union was written
// out by hand in the pay-run screen's dropdown, the web API client, the route's
// zod schema and the service's input type. `calculateStatutoryPay` priced ShPP
// and SPBP, the `StatutoryPayType` column held them and the FPS filed them —
// and none of the four unions listed them, so no run could carry either.
//
// Anything that accepts a statutory pay type imports this. Adding a payment
// then means adding it here, and TypeScript finds the places that must follow.
//
// Mirrors the `StatutoryPayType` enum in schema.prisma, which is the authority
// on what the column will store; the web cannot import Prisma, which is why
// this lives in the shared package rather than being generated.
// ------------------------------------------------------------------------

export type StatutoryPayType =
  | 'ssp'   // sickness
  | 'smp'   // maternity
  | 'spp'   // paternity
  | 'sap'   // adoption
  | 'sncp'  // neonatal care
  | 'shpp'  // shared parental — FPS data item 73
  | 'spbp'; // parental bereavement — FPS data item 212

export const STATUTORY_PAY_TYPES: readonly StatutoryPayType[] = [
  'ssp', 'smp', 'spp', 'sap', 'sncp', 'shpp', 'spbp',
] as const;

/** What a person calls each one. Used for the pay-run dropdown. */
export const STATUTORY_PAY_TYPE_LABELS: Record<StatutoryPayType, string> = {
  ssp: 'SSP',
  smp: 'SMP',
  spp: 'SPP',
  sap: 'SAP',
  sncp: 'SNCP',
  shpp: 'ShPP',
  spbp: 'SPBP',
} as const;

export type SubscriptionTier = 'base' | 'plus' | 'customised';

export const SUBSCRIPTION_TIERS: readonly SubscriptionTier[] = [
  'base',
  'plus',
  'customised',
] as const;

/**
 * Ordinal rank — used by feature-gate checks. A tenant on `plus` has access
 * to anything requiring `base` or `plus`. `customised` is the top tier.
 *
 * Note: in the new 3-tier model `base` is the BASE but is the full
 * operational platform. `plus` adds value-add modules (ISO/FORS/CIA).
 * `customised` is bespoke enterprise.
 */
export const TIER_RANK: Record<SubscriptionTier, number> = {
  base: 1,
  plus: 2,
  customised: 3,
} as const;

export function hasTierAccess(
  current: SubscriptionTier,
  required: SubscriptionTier,
): boolean {
  return TIER_RANK[current] >= TIER_RANK[required];
}

// ------------------------------------------------------------------------
// Country & locale — kept loose here; the full CountryPack type lives in
// @jeanieiq/country-packs. These are just the scalar identifiers used
// throughout the app (tenant record, JWT claims, etc).
// ------------------------------------------------------------------------

/** ISO 3166-1 alpha-2 — kept as string so we don't lock the union in Session 0.1 */
export type CountryCode = string;

/** The region inside a country (UK nation, Australian state or territory): see countryRegions.ts. */
export type { Nation, UkNation, AuState } from './countryRegions';

/** ISO 4217 — kept as string for the same reason */
export type CurrencyCode = string;

/** IETF language tag, e.g. 'en-GB' */
export type Locale = string;

// ------------------------------------------------------------------------
// MFA secret encoding — Session 0.3
//
// The `users.mfaSecret` column is a single TEXT field in the DB. We encode
// both the TOTP base32 secret and the bcrypt-hashed backup codes as a
// JSON blob so both survive in one column without a schema change.
//
// Legacy rows (pre-MFA-redesign or migrated in) may contain a bare base32
// secret as a plain string. parseMfaSecret() treats that as a valid value
// with an empty backupCodes array.
//
// If the MFA secret storage ever needs to move to a dedicated table, do
// the migration via a worker that reads every row, calls parseMfaSecret,
// writes to the new table, then clears users.mfaSecret.
// ------------------------------------------------------------------------

export interface MfaSecretPayload {
  /** TOTP base32 secret (speakeasy.generateSecret().base32) */
  secret: string;
  /** bcrypt-hashed backup codes — one-time use, each consumed on login */
  backupCodes: string[];
}

/**
 * Decode a users.mfaSecret column value. Returns null if the column is
 * empty / null / undefined (user has no MFA set up).
 *
 * Accepts two shapes:
 *   1. JSON:  `{"secret":"BASE32...","backupCodes":["$2b$10$..."]}`
 *   2. Legacy plain base32 string: `"JBSWY3DPEHPK3PXP"`
 *
 * The legacy form produces `{ secret, backupCodes: [] }`.
 */
export function parseMfaSecret(raw: string | null | undefined): MfaSecretPayload | null {
  if (raw == null || raw === '') return null;

  const trimmed = raw.trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        'secret' in parsed &&
        typeof (parsed as { secret: unknown }).secret === 'string'
      ) {
        const obj = parsed as { secret: string; backupCodes?: unknown };
        const backupCodes: string[] = Array.isArray(obj.backupCodes)
          ? obj.backupCodes.filter((c): c is string => typeof c === 'string')
          : [];
        return { secret: obj.secret, backupCodes };
      }
    } catch {
      // fall through — treat malformed JSON as a corrupt row
      return null;
    }
    return null;
  }

  // Legacy plain base32
  return { secret: trimmed, backupCodes: [] };
}

/**
 * Encode an MfaSecretPayload to the JSON form stored in users.mfaSecret.
 * Always produces the modern shape even if backupCodes is empty.
 */
export function encodeMfaSecret(payload: MfaSecretPayload): string {
  return JSON.stringify({
    secret: payload.secret,
    backupCodes: payload.backupCodes,
  });
}

/**
 * 2026-06-10 — Phase 2 per-user sidebar permissions (Shane). The canonical
 * sidebar labelKeys a director can deny per user (users.denied_sections —
 * same vocabulary as tenant.hidden_sections). The web derives its checklist
 * from the Sidebar nav itself; the API validates writes against THIS list,
 * so any drift between the two surfaces as a 422 on save rather than a
 * silently-ignored key.
 */
export const SIDEBAR_SECTION_KEYS = [
  'admin_hub',
  /*
   * 'dashboard' WAS HERE until 2026-09-23. Reagan: every user on the platform
   * has a dashboard. It is not a section a director grants, so offering it on
   * the permissions checklist meant offering a tick that either did nothing or
   * took away the landing page. What a person SEES on it is decided by the
   * sections below, through the widget catalogue.
   *
   * section-catalogue-guard carries a matching entry: the sidebar has a
   * Dashboard item that is deliberately not grantable.
   */
  'accreditations',
  'contracts_legal',
  // The digital asset register and the incident/breach record (2026-10-21).
  'information_security',
  'glossary',
  'hr_people',
  'rota_shifts',
  'training_records',
  'holiday',
  // Appraisals & Discipline sits between Holiday and Health & Safety
  // (Shane, 2026-08-21). Appraisals moved here out of Training Records.
  'appraisals_discipline',
  'health_safety',
  'plant_equipment',
  'fleet_transport',
  'building_maintenance',
  'hire_management',
  'lettings_management',
  /*
   * 2026-08-22 - the same fault as motor_trade below, found six more times.
   *
   * Shane spotted Compliance Gate sitting in the admin catalogue as a section
   * it is not, and asked for the sidebar to be audited properly rather than
   * one symptom fixed. Comparing the sidebar's own top-level items against
   * this list turned up six sections a tenant can see and open but that no
   * director could grant to a user: tenantUserService rejects any key not
   * listed here with 422 BAD_SECTION, so each was director-only with no way to
   * change it.
   *
   * plant_fleet_register is the one we built ourselves last week, which is the
   * point: this keeps happening because the four lists are maintained by hand.
   * `section-catalogue-guard.test.ts` now fails when they drift.
   */
  'plant_fleet_register',
  'plant_calibration',
  'am_vehicles',
  'am_schedule',
  'pharmacy',
  'hospitality',
  // 2026-08-22 — motor_trade was MOUNTED with section() but was never a key
  // here, and tenantUserService rejects any key not on this list with 422
  // BAD_SECTION. So the gate refused every non-director and no director could
  // grant it: the section was director-only with no way to change that.
  //
  // Reagan closed a real hole when she moved the mount off a bare app.use
  // (any authenticated user had been reaching finance applications). The fix
  // landed half-connected because a section has to be declared in FOUR places
  // by hand and nothing checked they agreed. This is the fourth.
  'motor_trade',
  /*
   * STANDARD SALES & JOBS (2026-08-29) - SPEC-51. The standard operational
   * vertical: enquiry, quotation, job, invoice.
   *
   * It gets its OWN key rather than reusing enquiries_quotations and
   * jobs_projects, because the verticals are alternatives rather than layers -
   * Shane: *"they are their own true verticals"*. A tenant has one operational
   * area, and identifying it by two keys that also describe the switchboard's
   * screens would leave it half-attached to what it replaces.
   */
  'standard_sales',
  /*
   * SALES (2026-10-13) — the OFFICE sector's operational section.
   *
   * Reagan: *"this sales section is for more office sector that we are
   * building right now"*, and *"this would be for customers that only need
   * sales that dont have jobs"*.
   *
   * ENQUIRIES AND QUOTATIONS, NO JOBS. A business selling a service — ours
   * included — does not track a job afterwards: the platform is online and
   * people onboard themselves. Standard Sales & Jobs keeps its seven screens
   * for manufacturing, where a job is a real thing somebody has to watch.
   *
   * IT IS NOT A SECOND SET OF RECORDS. Both sections read the SAME enquiries
   * and quotations through the SAME `/api/standard-sales` routes, which name
   * both keys — "a route genuinely shared between operational verticals names
   * each section entitled to reach it, and holding any one is enough". A
   * tenant sees one section or the other; the data underneath is identical, so
   * nothing has to be migrated if a customer's shape changes.
   *
   * Sector-tagged to `office` in SIDEBAR_SECTION_CATALOG, so it follows the
   * tenant's sector rather than needing granting by hand.
   */
  'sales',
  // Marketing: library of files, planned social posts and their calendar.
  'marketing',
  /*
   * SWITCHBOARD BUILDER (2026-08-22) - a SECTION, not a tier.
   *
   * Shane: *"we just move the 2 customised into switchboard builder on the
   * sidebar to be selected"*, and *"like motor trade or pharmacy"*.
   *
   * It used to be the Customised tier of Enquiries & Quotations, which meant
   * one customer's bespoke build was handed out by picking a LEVEL. Any tenant
   * granted Customised Enquiries got it - jeanieiq-master is on the customised
   * plan and could have opened it.
   *
   * As a section it uses the machinery every other section already has: a
   * grant per tenant, a sector preset, a per-user permission, an API gate.
   * Nothing new was needed to own it properly.
   */
  'switchboard_builder',
  /*
   * THE MAIL ROOM (2026-10-13) — drop an email in, read what it says, and say
   * what it is: an enquiry, an issue, a customer, or nothing.
   *
   * A SECTION, NOT A SECTOR, and that distinction was the first thing decided.
   * The ask was "a sector just for our tenant", but sector is the wrong lever:
   * `Tenant.sector` is a free String with no enum behind it, it only ever SEEDS
   * the section list at tenant creation, and our own build tenant proves the
   * point by running Pharmacy in full while carrying sector='manufacturing'.
   * Switchboard Builder above is the precedent — one company's bespoke build,
   * owned properly by giving it a key and granting it per tenant.
   *
   * So nothing here names a tenant. Which company gets a front desk is a tick in
   * JeanieIQ Admin, exactly like every other section.
   */
  'front_desk',
  'customer_crm',
  /*
   * `enquiries_quotations` and `jobs_projects` WERE HERE until 2026-08-29,
   * retired in SPEC-51 Stage 10 (Shane: *"take them off please"*).
   *
   * `standard_sales` above replaces both for a tenant with no operational
   * vertical of its own; a tenant WITH one runs that instead. Removed from the
   * sidebar and the admin catalogue in the same commit - section-catalogue-guard
   * requires all three to agree, which is what stops a half-removal.
   *
   * THEIR API ROUTES SURVIVE THEM, deliberately. `/api/jobs`, `/api/production`
   * and `/api/rams-templates` now name every section entitled to reach them -
   * Switchboard Builder's Engineering screens and ERP & Accounts among them - so
   * retiring the key here takes nothing away. That gate had to be fixed FIRST;
   * doing this without it would have kept working for directors, who are exempt,
   * while breaking for everybody else.
   */
  'stock_management',
  'approved_suppliers',
  'erp_accounts',
  'payroll',
  'sub_contract',
  'cashflow',
  'audit_pack',
  'directory',
  'document_register',
] as const;

export type SidebarSectionKey = (typeof SIDEBAR_SECTION_KEYS)[number];

/**
 * Sections a company holds only when it is ticked for them in Platform Admin.
 * A company with no show list yet (new, or emptied) holds every other section
 * by the fallback, but not these. Marketing is JeanieIQ's own for now.
 */
export const OPT_IN_SECTION_KEYS: readonly SidebarSectionKey[] = ['marketing'];

/**
 * 2026-09-08 — the Dashboard TABS a director may grant, per user.
 *
 * DELIBERATELY NOT IN `SIDEBAR_SECTION_KEYS`. These are tabs inside one
 * section, not sections. `section-catalogue-guard` asserts that every grantable
 * key is a real sidebar entry, and `keepKnownSections` drops anything that is
 * not — so putting them there would break the guard AND silently discard the
 * tick on save. They live in their own list, stored in `users.dashboard_tabs`.
 *
 * DIRECTOR IS NOT A KEY. Shane, 2026-09-08: *"For a director he sees
 * everything"*. Directors are exempt in code and never consult this list, which
 * is the same rule that keeps an owner from locking themselves out of the
 * sidebar. Granting a director tab to a non-director is therefore not a thing
 * that can be expressed — the Director Dashboard is the director's, and what a
 * manager or accounts clerk gets is decided by which of these two they hold.
 */
/*
 * 'manager' WAS HERE until 2026-09-23.
 *
 * It existed to decide whether somebody was shown the Manager Dashboard tab,
 * and the Director/Manager/Accounts tab strip was removed on 2026-09-22 — the
 * widget catalogue does that job per person now, rather than per category. So
 * nothing read it, and a director ticking it was granting nothing at all.
 *
 * `accounts` stays because it still does something: it gates every money
 * widget in the catalogue, and it is what `redactFinancials` keys off when it
 * blanks the figures on the wire.
 *
 * A one-value list looks odd, and that is a fair observation rather than a
 * mistake — whether the money gate should stay a "dashboard tab" at all, or
 * become a named permission of its own, is a question for Shane and not one
 * to answer by quietly keeping a dead key for company.
 */
export const DASHBOARD_TAB_KEYS = ['accounts'] as const;

export type DashboardTabKey = (typeof DASHBOARD_TAB_KEYS)[number];

// ------------------------------------------------------------------------
// Content taxonomy — DH-CONTENT-PROFILE-001 Phase 1 (the shared spine)
//
// One canonical vocabulary for tagging content (awareness courses, guidance
// areas, toolbox talks) and for the per-tenant sector/role PROFILE that
// switches what each tenant sees + enforces. Two axes:
//   • Sector       — the line(s) of business a tenant operates in.
//   • Role category — which group of people within a tenant a thing applies to.
//
// Matching rule (taxonomyTagsApply): content with NO tags, or tagged
// TAXONOMY_ALL, applies to everyone (universal — nothing hides until it is
// deliberately tagged). Otherwise it applies only where its tags intersect the
// profile's. This mirrors apps/api/src/lib/roleCategoryMatcher's semantics so
// the established role axis is unchanged, and both axes share ONE matcher so
// they can never drift apart.
// ------------------------------------------------------------------------

/** Tag value meaning "applies to all" on either side of a taxonomy match. */
export const TAXONOMY_ALL = 'all';

export const SECTORS = [
  'construction',
  'electrical',
  'fleet_transport',
  'manufacturing',
  'office',
  'pharmacy',
  'hospitality',
  'motor_trade',
  'care',
] as const;
export type Sector = (typeof SECTORS)[number];

export const SECTOR_LABELS: Record<Sector, string> = {
  construction: 'Construction',
  electrical: 'Electrical',
  fleet_transport: 'Fleet / Transport',
  manufacturing: 'Manufacturing',
  office: 'Office',
  pharmacy: 'Pharmacy',
  hospitality: 'Hospitality',
  motor_trade: 'Motor Trade',
  care: 'Care',
};

/**
 * Role-category axis — the established `roleCategoryMatcher` vocabulary
 * (`Role.categoryTags` + course/talk `roleCategoryTags`). `TAXONOMY_ALL`
 * ('all') is the universal value and is intentionally NOT listed here (it is
 * a wildcard tag, not a selectable category).
 */
export const ROLE_CATEGORIES = ['office', 'fleet_site', 'patient_facing'] as const;
export type RoleCategory = (typeof ROLE_CATEGORIES)[number];

export const ROLE_CATEGORY_LABELS: Record<RoleCategory, string> = {
  office: 'Office',
  fleet_site: 'Fleet / Site',
  // The Core Skills Training Framework splits its statutory subjects into
  // Level 1 for all staff and Level 2 for those in direct patient contact.
  // Without a category for the latter, a Level 2 course can only be seeded as
  // required-for-everyone — wrong data rather than merely imprecise data.
  patient_facing: 'Patient facing',
};

/**
 * Regulated community-pharmacy job roles.
 *
 * Three deliberate departures from the values `pharmacy_staff.role` has
 * carried, each of which made a real requirement impossible to express:
 *
 *  - **`dispenser` is gone.** It collapsed a dispensing assistant and a
 *    medicines counter assistant, who take different GPhC course routes and
 *    therefore carry different training requirements.
 *  - **`delivery-driver` is new.** The 2020 revision of the GPhC's education
 *    and training requirements brought delivery drivers into scope for
 *    support-staff training. Driver training is among the most commonly
 *    missing items in a pharmacy's records, so omitting the role would leave
 *    a gap that looks like completeness.
 *  - **`locum` is not here.** It is an engagement mode, not a job. A locum
 *    pharmacist must inherit every pharmacist requirement; while locum was a
 *    role, it could not.
 *
 * Scope is community pharmacy. Hospital and primary-care-network roles, and
 * consultant pharmacist credentialing, are deliberately absent.
 */
export const PHARMACY_ROLES = [
  'pharmacist',
  'trainee-pharmacist',
  'pharmacy-technician',
  'trainee-pharmacy-technician',
  'dispensing-assistant',
  'medicines-counter-assistant',
  'delivery-driver',
] as const;
export type PharmacyRole = (typeof PHARMACY_ROLES)[number];

export const PHARMACY_ROLE_LABELS: Record<PharmacyRole, string> = {
  'pharmacist': 'Pharmacist',
  'trainee-pharmacist': 'Trainee pharmacist',
  'pharmacy-technician': 'Pharmacy technician',
  'trainee-pharmacy-technician': 'Trainee pharmacy technician',
  'dispensing-assistant': 'Dispensing assistant',
  'medicines-counter-assistant': 'Medicines counter assistant',
  'delivery-driver': 'Delivery driver',
};

/**
 * How a person is engaged. Orthogonal to their role — the axis that lets a
 * locum pharmacist be a pharmacist for every requirement purpose.
 */
export const ENGAGEMENT_MODES = ['employed', 'locum', 'relief'] as const;
export type EngagementMode = (typeof ENGAGEMENT_MODES)[number];

/**
 * Map a legacy `pharmacy_staff.role` value onto the canonical vocabulary.
 *
 * Three of the four legacy values map cleanly. `dispenser` returns
 * 'ambiguous' on purpose: it splits into medicines-counter-assistant or
 * dispensing-assistant on a distinction only the tenant knows, and the two
 * take different GPhC course routes. Guessing would write compliance data
 * that is wrong but looks right — strictly worse than leaving the row for a
 * human, because nobody would ever go back and check it.
 *
 * Anything unrecognised is ambiguous too. A backfill that silently drops
 * values it does not understand is how a migration reports success over data
 * it never touched.
 */
export function mapLegacyPharmacyRole(
  role: string,
): { role: PharmacyRole; engagement: EngagementMode } | 'ambiguous' {
  switch (role.trim().toLowerCase()) {
    case 'pharmacist':
      return { role: 'pharmacist', engagement: 'employed' };
    case 'technician':
      return { role: 'pharmacy-technician', engagement: 'employed' };
    case 'locum':
      return { role: 'pharmacist', engagement: 'locum' };
    default:
      return 'ambiguous';
  }
}

/**
 * Canonical taxonomy match — used for BOTH the sector and role axes, the
 * per-tenant profile filter, and the gate's course enforcement, so they cannot
 * drift apart.
 *
 * Universal (true for everyone) when the content side is empty or contains
 * TAXONOMY_ALL ("untagged = applies to all"); likewise a profile side that is
 * empty or contains TAXONOMY_ALL accepts everything. Otherwise true iff the two
 * sets intersect. Preserves the pre-existing role-matcher behaviour exactly.
 */
export function taxonomyTagsApply(
  contentTags: readonly string[] | null | undefined,
  profileTags: readonly string[] | null | undefined,
): boolean {
  const content = contentTags ?? [];
  const profile = profileTags ?? [];
  if (content.length === 0 || content.includes(TAXONOMY_ALL)) return true;
  if (profile.length === 0 || profile.includes(TAXONOMY_ALL)) return true;
  return content.some((tag) => profile.includes(tag));
}

// ------------------------------------------------------------------------
// Guidance house style (spec-45 Phase A)
//
// The block vocabulary, the renderer and the structural validator that own
// how a guidance body is built. Nothing outside guidanceBlocks.ts emits
// guidance HTML — a caller chooses blocks, this module decides the markup.
// ------------------------------------------------------------------------

export * from './guidanceBlocks';

// ------------------------------------------------------------------------
// Dated claims and unresolved markers (spec-45 Phase E)
//
// The honest limit of the engine, as code: it cannot tell a right date from a
// wrong one, so it insists a dated claim names a source and refuses to publish
// a [CHECK: ...] the drafter left behind.
// ------------------------------------------------------------------------

export * from './guidanceClaims';

// ------------------------------------------------------------------------
// The card-field envelope (spec-45 Phase G)
//
// ONE definition of the standard, shared by the AI draft schema, the save
// path and the editor. Previously the schema bounded the model and the save
// path bounded nothing, so a draft could be cut to three key points and
// stored — the standard was enforced on the machine and not the person.
// ------------------------------------------------------------------------

export * from './guidanceCard';

export * from './guidanceParse';

// ISO clause reference data — shared by the API (audit-review export) and the
// web (clause map + guidance tabs). One copy, imported twice.
export * from './isoClauseMap';
export * from './jobStageFlow';
export * from './satisfactionSurvey';
export * from './capaProduct';
export * from './appraisalForm';
export * from './emissionFactors';
export * from './managementReviewAgenda';
export * from './auditSchedule';
export * from './certificationAudit';
export * from './capaRag';
// The control line every printed document carries: web screens and server PDFs read one copy.
export * from './documentControlFooter';
// The BMS manual's grids and links, read the same way on screen and in its PDF.
export * from './manualBlocks';
export * from './objectiveMeasurement';

/*
 * What a tenant is ACCREDITED TO — the four ISO standards plus the three FORS
 * grades. Deliberately separate from `IsoStandard` above, which is the ISO
 * CLAUSE vocabulary and a Prisma enum on the evidence tables. See the file
 * header for why the two cannot be one list.
 */
export * from './accreditationStandards';
export * from './addonUnlocks';

/*
 * The text of the platform's own forms. One source of truth for wording an
 * employee is legally asked, shared by the screens that ask it and the register
 * that has to render it as a controlled document. See the file header.
 */
export * from './platformForms';

/*
 * What the asset registers DISPLAY — labels and resolvers shared by the screen
 * and the printed document, so a print says what the page says (2026-08-30).
 * Shane: *"all of them need to print what they have in their display"*, and on
 * how: *"not reinvented"*.
 */
export * from './registerDisplay';

/*
 * The HGV drivers'-hours duty rules, shared by the planner screen, the printed
 * week and the API (2026-09-03).
 *
 * Same principle as registerDisplay above — Shane, on prints: *"not
 * reinvented"* — and for a stronger reason here. These are LEGAL limits: EU
 * 561 driving breaks, the Road Transport (Working Time) Regs 2005 break, and
 * the 13h/15h duty window. Two implementations that can disagree is the fault
 * this section already carries once, where the web engine and the API's
 * getWeek disagree about which day a rest type belongs to.
 */
export * from './hgvDuty';
/* NHS income from the Schedules of Payments: one name, since its own are too general to sit loose here. */
export * as nhsIncome from './nhsIncome';
/* PH-REG-11: the PCS protocol's per-method measurement rules, shared by the API gate and the screen. */
export * from './pcsProtocol';
/* PH-REG-01: the Pharmacy First pathway reference, from the NHS clinical pathways. */
export * from './pfPathwayReference';

/*
 * Emissions standard and powertrain — two different facts about a vehicle,
 * shared by the form that writes them and the register that colours them
 * (2026-09-03). One vocabulary, both sides: a value the form can write but the
 * badge cannot band would render as "unknown" on a vehicle somebody had just
 * told us about.
 */
export * from './vehicleEmissions';

/*
 * A recurring service date (2026-09-04). The stored date is an ANCHOR and the
 * next occurrence is derived from it, so nothing has to run on a schedule for
 * the register to be right and the date cannot drift. Shared because the API
 * derives it and the form writes the interval — the two must agree on what an
 * interval means.
 */
export * from './serviceSchedule';

/**
 * What counts as a planned Site Works day. Shared for the same reason as the
 * service schedule above: the screen decides what to draw and the assignment
 * service decides whether a day is taken, and when those were written twice
 * they disagreed in both directions — a day showing free that refused a
 * booking, and a day showing planned that was silently overwritten.
 */
export * from './licenceEndorsements';
export * from './hgvPlannerDay';
export * from './sitePlannerDay';

/*
 * HS-EXPORT-SOURCE-001 (2026-09-16) - the marker format a register-native
 * Health & Safety record is stored in. Shared because the WEB writes it and
 * the API reads it for the CSV export, and two implementations of one format
 * agree until they do not.
 */
export * from './hsRegisterMarkers';
// What a document's {{tokens}} fill with, and the roll-call's marshal rule (TODO 260).
export * from './documentTokens';
export * from './supplierVisit';
export * from './supplierQuestionnaire';
export * from './documentEmailWording';
export * from './companyBlock';
export * from './personName';

/*
 * The countries Platform Admin works in (TODO 528) - one list for the country
 * cards, Country Configuration and the API's check on a working country.
 */
export * from './platformCountries';
export { COUNTRY_REGIONS, ALL_REGION_CODES, regionsOf, regionWord, regionName, regionCodesFor, regionBelongsTo, defaultRegionFor, type CountryRegion } from './countryRegions';

/*
 * The Platform Admin sections a permission level ticks (TODO 530) - one list for
 * the sidebar, the level editor and the API's check on every admin route.
 */
export * from './platformAdminSections';

// Clinical modules hidden until the safety case covers them (CS-02 HM-01).
export * from './clinicalModules';
export * from './employmentTypes';
export * from './contractTypes';
export * from './gateBlockTypes';
export * from './isoEvidenceSources';

/*
 * The Payroll > Reports > Pay Report's columns and shape (TODO 1400) - one list
 * for the screen's tick boxes, the CSV and the API's PDF.
 */
export * from './payReportColumns';
export * from './payElementsReport';
export * from './payVarianceReport';

export * from './sidebarSubTabs';
export * from './complianceReportAreas';

// The leave types a leave request carries and their labels — one list (TODO 570, 573).
export * from './leaveTypes';

// A working pattern as text, for the HR card and the employment contract.
export * from './workingPatternText';

// Reading an export back in: step over the print stamp at the top of a CSV.
export * from './csvPrintStamp';

// The colours a person can pick for the Sales calendar.
export * from './salesColours';
